import React, { useState, useEffect } from 'react';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Trophy, Star, Medal, Crown, PawPrint, ChevronUp } from 'lucide-react';
import { Progress } from '../../components/ui/progress';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACHIEVEMENT_ICONS = {
  star: Star,
  trophy: Trophy,
  medal: Medal,
  graduation: Crown,
};

export default function OwnerLeaderboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    try {
      const [lbRes, statsRes] = await Promise.all([
        axios.get(`${API}/leaderboard`),
        axios.get(`${API}/leaderboard/user/${user.id}`)
      ]);
      setLeaderboard(lbRes.data);
      setMyStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { bg: 'bg-amber-400', text: 'text-white', label: '1st' };
    if (rank === 2) return { bg: 'bg-gray-300', text: 'text-gray-700', label: '2nd' };
    if (rank === 3) return { bg: 'bg-amber-600', text: 'text-white', label: '3rd' };
    return { bg: 'bg-edupaws-navy/10', text: 'text-edupaws-navy', label: `#${rank}` };
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-float">
            <Trophy className="w-12 h-12 text-amber-400" />
          </div>
        </div>
      </OwnerLayout>
    );
  }

  return (
    <OwnerLayout>
      {/* Header */}
      <div className="glass-card p-8 rounded-3xl mb-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-50/50 via-transparent to-edupaws-sage/10"></div>
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full mb-4">
            <Trophy className="w-5 h-5 text-amber-500" />
            <span className="font-body font-medium text-amber-700">Family Leaderboard</span>
          </div>
          <h1 className="font-heading text-4xl font-bold text-edupaws-navy mb-3">
            Training Champions
          </h1>
          <p className="text-gray-500 font-body max-w-xl mx-auto">
            See how your family's training stacks up! Earn points by completing videos and series.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* My Stats */}
        {myStats && (
          <div className="lg:col-span-1 space-y-6">
            {/* Points Card */}
            <div className="glass-card p-6 rounded-2xl">
              <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">Your Family Stats</h2>
              <div className="text-center mb-4">
                <div className="font-heading text-5xl font-bold text-edupaws-navy">{myStats.total_points}</div>
                <div className="text-sm text-gray-500 font-body">Total Points</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-sage p-3 rounded-xl text-center">
                  <div className="font-heading text-xl font-bold text-edupaws-navy">{myStats.dog_count}</div>
                  <div className="text-xs text-gray-500 font-body">Dogs</div>
                </div>
                <div className="glass-sage p-3 rounded-xl text-center">
                  <div className="font-heading text-xl font-bold text-edupaws-navy">{myStats.family_member_count}</div>
                  <div className="text-xs text-gray-500 font-body">Members</div>
                </div>
              </div>
            </div>

            {/* Dog Progress */}
            {myStats.dogs.map((dog) => (
              <div key={dog.id} className="glass-card p-4 rounded-2xl" data-testid={`my-dog-stats-${dog.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  {dog.photo_url ? (
                    <img src={dog.photo_url} alt={dog.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 glass-sage rounded-full flex items-center justify-center">
                      <PawPrint className="w-5 h-5 text-edupaws-navy" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-heading font-bold text-edupaws-navy">{dog.name}</div>
                    <div className="text-xs text-gray-500 font-body">{dog.breed}</div>
                  </div>
                  <div className="font-heading font-bold text-edupaws-sage">{dog.points} pts</div>
                </div>
                <Progress value={dog.progress_percent} className="h-2 mb-2" />
                <div className="flex justify-between text-xs text-gray-500 font-body">
                  <span>{dog.videos_completed} videos completed</span>
                  <span>{dog.series_completed} series done</span>
                </div>
              </div>
            ))}

            {/* Achievements */}
            {myStats.achievements.length > 0 && (
              <div className="glass-card p-6 rounded-2xl">
                <h2 className="font-heading text-lg font-bold text-edupaws-navy mb-4">Achievements</h2>
                <div className="space-y-3">
                  {myStats.achievements.map((ach, i) => {
                    const IconComp = ACHIEVEMENT_ICONS[ach.icon] || Star;
                    return (
                      <div key={i} className="flex items-center gap-3 glass-sage p-3 rounded-xl" data-testid={`achievement-${i}`}>
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <IconComp className="w-5 h-5 text-amber-500" />
                        </div>
                        <div>
                          <div className="font-heading font-bold text-edupaws-navy text-sm">{ach.title}</div>
                          <div className="text-xs text-gray-500 font-body">{ach.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Leaderboard */}
        <div className="lg:col-span-2">
          <div className="glass-card rounded-2xl overflow-hidden">
            <div className="p-6 border-b border-white/30">
              <h2 className="font-heading text-xl font-bold text-edupaws-navy">Rankings</h2>
              <p className="text-sm text-gray-500 font-body">10 pts per video, 100 bonus pts per completed series</p>
            </div>
            <div className="divide-y divide-white/20">
              {leaderboard.length === 0 ? (
                <div className="p-12 text-center">
                  <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-body">No rankings yet. Start training to be first!</p>
                </div>
              ) : (
                leaderboard.map((entry) => {
                  const badge = getRankBadge(entry.rank);
                  const isMe = entry.owner_id === user?.id;

                  return (
                    <div
                      key={entry.owner_id}
                      data-testid={`leaderboard-entry-${entry.rank}`}
                      className={`p-4 flex items-center gap-4 transition-colors
                        ${isMe ? 'bg-edupaws-sage/10' : 'hover:bg-white/20'}`}
                    >
                      {/* Rank */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold text-sm ${badge.bg} ${badge.text}`}>
                        {entry.rank <= 3 ? (
                          entry.rank === 1 ? <Crown className="w-5 h-5" /> :
                          entry.rank === 2 ? <Medal className="w-5 h-5" /> :
                          <Star className="w-5 h-5" />
                        ) : badge.label}
                      </div>

                      {/* Family Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-heading font-bold text-edupaws-navy truncate">
                            {entry.owner_name}
                          </span>
                          {isMe && (
                            <span className="text-xs bg-edupaws-navy text-white px-2 py-0.5 rounded-full font-body">You</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 font-body">
                          <span>{entry.dog_count} dog{entry.dog_count !== 1 ? 's' : ''}</span>
                          <span>{entry.total_videos_completed} videos</span>
                          <span>{entry.total_series_completed} series</span>
                        </div>
                      </div>

                      {/* Dogs */}
                      <div className="hidden sm:flex -space-x-2">
                        {entry.dogs.slice(0, 3).map((dog, i) => (
                          dog.photo_url ? (
                            <img key={i} src={dog.photo_url} alt={dog.name} className="w-8 h-8 rounded-full object-cover border-2 border-white" />
                          ) : (
                            <div key={i} className="w-8 h-8 rounded-full bg-edupaws-sage/20 border-2 border-white flex items-center justify-center">
                              <PawPrint className="w-4 h-4 text-edupaws-sage" />
                            </div>
                          )
                        ))}
                      </div>

                      {/* Points */}
                      <div className="text-right">
                        <div className="font-heading text-xl font-bold text-edupaws-navy flex items-center gap-1">
                          {entry.total_points}
                          <ChevronUp className="w-4 h-4 text-edupaws-sage" />
                        </div>
                        <div className="text-xs text-gray-500 font-body">points</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </OwnerLayout>
  );
}
